import React, { useMemo, useState } from "react";
import { Box, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fmtKRWFull } from "../../api/financeApi";
import { statusInfo } from "../../constants/cashPlanStatus";

// 상태 메타는 constants/cashPlanStatus.js 단일 원천 사용(중복 정의 제거).
const si = statusInfo;
const fmtDate = (d) => d ? String(d).slice(5).replace("-", "/") : "—";

/**
 * 지급 예정 스케줄 보드 — 향후 30일 이내 지출 예정(미완료) 건.
 * 날짜순 / 거래처순 두 뷰 토글. 홀딩(자금 부족으로 미뤄둔 큰 지출)·연체(예정일 지남)를 강조.
 * @param {Array} rows getCashFlow 응답의 rows (예정일·금액·거래처·상태·overdue 포함)
 */
export default function UpcomingPaymentsBoard({ rows = [] }) {
    const [view, setView] = useState("date");   // date | vendor

    const items = useMemo(() => {
        const horizon = new Date();
        horizon.setDate(horizon.getDate() + 30);
        const hz = horizon.getTime();
        return (rows || [])
            .filter(r => r.flowType === "EXPENSE" && r.status !== "COMPLETED"
                && r.scheduledDate && new Date(r.scheduledDate).getTime() <= hz)
            .sort((a, b) => (a.scheduledDate < b.scheduledDate ? -1 : 1));
    }, [rows]);

    const byVendor = useMemo(() => {
        const m = new Map();
        items.forEach(r => {
            const key = r.vendorName || r.title || "미지정";
            const cur = m.get(key) || { name: key, amount: 0, count: 0, hasHold: false, hasOverdue: false };
            cur.amount += r.amount || 0;
            cur.count += 1;
            if (r.status === "HOLD") cur.hasHold = true;
            if (r.overdue) cur.hasOverdue = true;
            m.set(key, cur);
        });
        return [...m.values()].sort((a, b) => b.amount - a.amount).slice(0, 8);
    }, [items]);

    const total = items.reduce((s, r) => s + (r.amount || 0), 0);
    const holdTotal = items.filter(r => r.status === "HOLD").reduce((s, r) => s + (r.amount || 0), 0);
    const overdueCnt = items.filter(r => r.overdue).length;

    return (
        <Box>
            {/* 요약 + 토글 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                    향후 30일 지급 예정 <b style={{ color: "#dc2626" }}>{fmtKRWFull(total)}</b> · {items.length}건
                    {holdTotal > 0 && <> · 홀딩 <b style={{ color: "#dc2626" }}>{fmtKRWFull(holdTotal)}</b></>}
                    {overdueCnt > 0 && <> · <b style={{ color: "#dc2626" }}>연체 {overdueCnt}건</b></>}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}
                    sx={{ "& .MuiToggleButton-root": { px: 1.4, py: 0.3, fontSize: 12, textTransform: "none" } }}>
                    <ToggleButton value="date">날짜순</ToggleButton>
                    <ToggleButton value="vendor">거래처순</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {items.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8", py: 2 }}>향후 30일 내 지급 예정이 없습니다.</Typography>
            ) : view === "date" ? (
                <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 320, overflowY: "auto" }}>
                    {items.slice(0, 40).map(r => {
                        const s = si(r.status);
                        return (
                            <Box key={r.rowKey ?? r.id} sx={{
                                display: "flex", alignItems: "center", gap: 1, py: 0.7,
                                borderBottom: "1px solid #f1f5f9",
                            }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: r.overdue ? "#dc2626" : "#475569", width: 44, flexShrink: 0 }}>
                                    {fmtDate(r.scheduledDate)}
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }} title={r.title}>{r.title}</Typography>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                        {[r.vendorName, r.deptName].filter(Boolean).join(" · ") || "—"}
                                    </Typography>
                                </Box>
                                <Chip label={s.label} size="small" sx={{ height: 20, fontSize: 10.5, bgcolor: s.bg, color: s.color, fontWeight: 700, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#dc2626", whiteSpace: "nowrap", width: 96, textAlign: "right", flexShrink: 0 }}>
                                    −{fmtKRWFull(r.amount)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {byVendor.map(v => (
                        <Box key={v.name} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.7, borderBottom: "1px solid #f1f5f9" }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }} title={v.name}>{v.name}</Typography>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                    {v.count}건{v.hasHold ? " · 홀딩 포함" : ""}{v.hasOverdue ? " · 연체 포함" : ""}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#dc2626", whiteSpace: "nowrap" }}>
                                −{fmtKRWFull(v.amount)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
